import { HubConnectionState } from '@microsoft/signalr';
import { action, computed, reaction, makeObservable } from 'mobx';
import { classroomJoinedState } from '../../../domain/services/ClassroomJoinedState';
import { Disposers, logger, toastrHelper, urlHelper } from '../../../lib/helpers';
import { ClassroomHubMessage, ClassroomRole } from '../models/classroom-models';
import { ClassroomVideoStore } from '../video/ClassroomVideoStore';
import { ClassroomTasks } from './classroom-tasks';
import { ClassroomJoinStore } from './ClassroomJoinStore';
import { ClassroomKeyValuesStore } from './ClassroomKeyValuesStore';
import { ClassroomStateStore } from './ClassroomStateStore';
import { ClassroomTaskStore } from './ClassroomTaskStore';
import { HubConnectionStore } from './HubConnectionStore';
import swal from 'sweetalert2';
import { debounce } from 'lodash';


export class ClassroomHubStore {
    readonly connection: HubConnectionStore;
    readonly join: ClassroomJoinStore;
    readonly state: ClassroomStateStore;
    readonly keyValues: ClassroomKeyValuesStore;
    readonly tasks: ClassroomTaskStore;
    readonly video: ClassroomVideoStore;
    private readonly disposers = new Disposers();


    constructor(readonly classroomId: number,
        readonly role: ClassroomRole,
        readonly isPreview: boolean,
        readonly isHomework: boolean) {
        makeObservable(this);
        this.connection = new HubConnectionStore();
        this.disposers.add(this.connection);
        this.join = new ClassroomJoinStore(classroomId, role, this.isPreview, this.isHomework);
        this.state = new ClassroomStateStore();
        this.keyValues = new ClassroomKeyValuesStore();
        this.tasks = new ClassroomTaskStore();
        this.video = new ClassroomVideoStore(this);
        this.connection.connection.on('message', this.onReceivedMessage);
        this.setup();
    }


    @action
    start() {
        this.connection.start();
    }

    @computed
    get version(): number | null {
        const classroomState = this.state.classroomState;
        return classroomState === null
            ? null
            : classroomState.Version;
    }

    @computed
    get lessonSnapshotId(): number | null {
        const classroomState = this.state.classroomState;
        return classroomState === null
            ? null
            : classroomState.LessonSnapshotId;
    }

    @computed
    get currentPageId(): number | null {
        const classroomState = this.state.classroomState;
        return classroomState === null
            ? null
            : classroomState.CurrentPageId;
    }

    @computed
    get currentPushableBlockId(): number | null {
        const classroomState = this.state.classroomState;
        return classroomState === null
            ? null
            : classroomState.CurrentPushableBlockId;
    }

    @computed
    get revealedBlockIds(): ReadonlySet<number> {
        return this.keyValues.revealedBlockIds;
    }

    @computed
    get homeworkBlockIds(): ReadonlySet<number> {
        return this.keyValues.homeworkBlockIds;
    }

    private setup() {
        classroomJoinedState.setProvider(this.join);
        this.disposers.add(() => classroomJoinedState.setProvider(null));

        this.disposers.add(
            reaction(
                () => this.connection.connectionState,
                this.onConnectionStateChange,
                { fireImmediately: true },
            ),
            reaction(
                () => this.join.state?.value,
                this.onJoinStateChange
            ),
            reaction(
                () => this.state.classroomState,
                this.onClassroomStateChange
            ),
            reaction(
                () => [this.tasks.any, this.tasks.active, this.tasks.state?.value],
                this.onTasksStateChange,
            ),
            reaction(
                () => this.keyValues.allChanges,
                this.onKeyValueUpdates
            ),
            reaction(
                () => this.keyValues.missingChangeStamps.size,
                this.onMissingChangeStamps
            ));
        // TODO: reactions
    }

    @action
    private onConnectionStateChange = () => {
        logger.log('ClassroomHubStore.onConnectionStateChange');
        const state = this.connection.connectionState;
        if (state === HubConnectionState.Connected) {
            this.join.join(this.connection.connection);
        }
        else {
            this.join.reset();
            this.state.clearClassroomState();
            this.keyValues.clear();
            this.tasks.clearAll();
        }
    };

    @action
    private onJoinStateChange = () => {
        const state = this.join.state;
        if (state !== null && state.isResolved) {
            this.onReceivedMessage(state.value);
        }
        if (this.join.joined)
            this.tasks.activate();
        else
            this.tasks.deactivate();
    };

    @action
    private onClassroomStateChange = () => {
        let state = this.state.classroomState;
        if (state === null) {
            logger.log('onClassroomStateChange clearing state');
            this.keyValues.clear();
        }
    };

    @action
    private onTasksStateChange = () => {
        if (!this.tasks.active)
            return;
        const state = this.tasks.state;

        if (state !== null) {
            if (state.isResolved) {
                this.onReceivedMessage(state.value);
            }
            if (state.isRejected) {
                this.onError(state.error);
            }
        }

        if (!this.tasks.active)
            return;
        this.tasks.runNext(this.connection.connection);
    };

    @action
    private onKeyValueUpdates = () => {
        this._onKeyValueUpdates();
    };

    private readonly _onKeyValueUpdates = debounce(() => {
        var self = this;
        if (!this.tasks.active || this.keyValues.changes.size === 0) {
            return;
        }
        if (this.isPreview) {
            return;
        }
        self.tasks.add(ClassroomTasks.updateKeyValues(self.keyValues.allChanges, this.isHomework));
    }, 2000);

    @action
    private onMissingChangeStamps = () => {
        if (!this.tasks.active || this.keyValues.missingChangeStamps.size === 0) {
            return;
        }
        if (this.isPreview) {
            return;
        }
        const missingChangeStamps = Array.from(this.keyValues.missingChangeStamps);
        this.tasks.add(ClassroomTasks.getKeyValuesPart(missingChangeStamps));
    };

    @action
    private onReceivedMessage = async (message: ClassroomHubMessage) => {
        logger.log('ClassroomHubStore.onReceivedMessage', message);

        switch (message.MessageType) {
            case 'bad-request':
                toastrHelper.error(message.Error);
                break;
            case 'disconnect':
                this.connection.dispose();
                this.connection.dontReconnectMessage = message.Error;
                break;
            case 'reset-connection':
                this.connection.reconnect();
                break;
            case 'classroom-joined':
                this.state.updateClassroomState(message.ClassroomState);
                this.keyValues.mergeComplete(message.KeyValues);
                this.video.setCredential(message.Credential);
                break;
            case 'classroom-state':
                this.state.updateClassroomState(message.ClassroomState);
                break;
            case 'classroom-key-values-updated':
                this.state.updateClassroomState(message.ClassroomState);
                this.keyValues.mergePartial(message.ChangeStamps, message.Changes);
                break;
            case 'classroom-key-values-part':
                this.keyValues.mergePartial(message.ChangeStamps, message.KeyValues);
                break;
            case 'debug':
                logger.log('payload', message.Payload);
                break;
            case 'simple-peer-signal':
                this.video.onReceivedSignal(message.Signal);
                break;
            case 'classroom-content-updated':
                await swal.fire({
                    html: 'Lesson content was updated.<br> The page will reload in 5 seconds',
                    timer: 5000,
                    didOpen: () => swal.showLoading(),
                    showCancelButton: false,
                    showCloseButton: false,
                    showConfirmButton: false,
                });
                window.location.reload();
                break;
            default:
                break;
        }
    };

    @action
    private onError(error?: any | undefined) {
        logger.log('ClassroomHubStore.onError', error);
    }

    dispose = () => {
        this.disposers.dispose();
    };
}
